/**
 * Gemeinsamer Lizenzhinweis und Kontaktprüfung für C1, PDF und Excel.
 * Der Dialog ändert keine Lizenzberechtigung. Er speichert geprüfte Angaben
 * als Entwurf und übernimmt den Versandzeitpunkt erst nach Backend-Bestätigung.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowRight,
  CheckCircle2,
  MessageSquare,
  ShoppingBag,
  X,
} from "lucide-react";
import { itx } from "../translationItx/itx";
import axios from "../utils/axios";
import storage from "../persistence/UniversalstorageHandler";
import {
  deliverProjectOffer,
  type OfferContact,
  type OfferScope,
  type ProjectOfferRequest,
} from "../models/ProjectOfferRequest";
import ToShopLicensesDialog from "./ToShopLicensesDialog";
import "@fontsource/montserrat/300.css";
import "./ProjectLicenseDialog.scss";

export type LicenseDialogContext = "documents" | "main-app";
const t = (key: string, text: string) => itx(`projectLicenseDialog.${key}`, text);

/** Shared license notice; deliberately does not grant or alter export entitlements. */
export function ProjectLicenseDialog({
  context,
  onContinue,
  onCancel,
}: {
  context: LicenseDialogContext;
  onContinue: () => void;
  onCancel: () => void;
}) {
  const [metadata] = useState(() => state.projectMetadata);
  const previous = metadata.offerRequests?.[metadata.offerRequests.length - 1];
  const [step, setStep] = useState<"license" | "contact" | "success">("license");
  const [scope, setScope] = useState<OfferScope>(previous?.scope ?? "planning");
  const [contact, setContact] = useState<OfferContact>(
    () =>
      previous?.contact ?? {
        name: state.loggedInUser?.name ?? "",
        email: state.loggedInUser?.email ?? "",
        phone: "",
        street: "",
        postalCode: "",
        city: "",
      },
  );
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [error, setError] = useState("");
  const [saveWarning, setSaveWarning] = useState(false);
  const [sent, setSent] = useState<ProjectOfferRequest>();
  const dialog = useRef<HTMLDivElement>(null);
  const draft = useRef<ProjectOfferRequest>();
  const documents = context === "documents";
  const continueLabel = documents
    ? t("createDocument", "Dokument erstellen")
    : t("continueKnxEngineering", "Weiter zum KNX-Engineering");

  useEffect(() => {
    const oldFocus = document.activeElement as HTMLElement | null;
    dialog.current
      ?.querySelector<HTMLElement>("[data-initial-focus]")
      ?.focus({ preventScroll: true });
    return () => {
      if (oldFocus?.isConnected) oldFocus.focus();
    };
  }, [step]);

  function updateContact(key: keyof OfferContact, value: string) {
    setContact((current) => ({ ...current, [key]: value }));
  }

  /** Speichert vor dem Versand einen Entwurf und übernimmt Erfolg nur vom Backend. */
  async function submit() {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    const clean = Object.fromEntries(
      Object.entries(contact).map(([key, value]) => [key, value.trim()]),
    ) as OfferContact;
    let request = draft.current;
    if (
      !request ||
      JSON.stringify(request.contact) !== JSON.stringify(clean) ||
      request.scope !== scope
    ) {
      request = {
        requestId: crypto.randomUUID(),
        projectId: metadata.id,
        projectName: metadata.name,
        scope,
        contact: clean,
        verifiedAt: new Date().toISOString(),
        status: "draft",
      };
      draft.current = request;
    }
    function record(next: ProjectOfferRequest) {
      metadata.offerRequests = [
        ...(metadata.offerRequests ?? []).filter((item) => item.requestId !== next.requestId),
        next,
      ];
      metadata.lastModifiedLocal = new Date();
      metadata.raiseOnChange("offerRequests");
    }
    try {
      record(request);
      await storage.saveProjectMetaData(metadata);
      // Standardroute des Angebots-Backends; die Entwicklungsumgebung kann sie überschreiben.
      const endpoint =
        import.meta.env.VITE_ESTADA_OFFER_REQUEST_ENDPOINT?.trim() || "/api/support/project-offer";
      if (!endpoint || !/^\/api\/[a-zA-Z0-9/_-]+$/.test(endpoint)) {
        throw new Error(
          t(
            "notConfigured",
            "Die Angebotsanfrage ist noch nicht freigeschaltet. Deine Angaben wurden als Entwurf im Projekt gespeichert; es wurde nichts versendet.",
          ),
        );
      }
      const result = await deliverProjectOffer(request, async (payload) => {
        const response = await axios.post(endpoint, payload, {
          timeout: 30000,
          baseURL: import.meta.env.VITE_ESTADA_OFFER_REQUEST_BACKEND_URL || undefined,
          headers: { "Idempotency-Key": payload.requestId },
        });
        if (response.data?.STATUS === "FAILURE")
          throw new Error(
            t("sendFailed", "Die Anfrage konnte nicht gesendet werden. Bitte versuche es erneut."),
          );
        return response.data?.DATA ?? response.data;
      });
      record(result);
      draft.current = result;
      setSent(result);
      // A successful delivery must never become a retryable send just because saving failed.
      try {
        await storage.saveProjectMetaData(metadata);
      } catch {
        setSaveWarning(true);
      }
      setStep("success");
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : typeof (failure as any)?.MESSAGE === "string"
            ? (failure as any).MESSAGE
            : t(
                "sendFailed",
                "Die Anfrage konnte nicht gesendet werden. Bitte versuche es erneut.",
              ),
      );
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  function field(key: keyof OfferContact, label: string, type = "text", wide = false) {
    return (
      <label className={wide ? "projectLicenseField--wide" : undefined} htmlFor={`offer-${key}`}>
        {label}
        <input
          id={`offer-${key}`}
          name={key}
          type={type}
          required
          disabled={busy}
          value={contact[key]}
          onChange={(event) => updateContact(key, event.target.value)}
          autoComplete={
            {
              name: "name",
              email: "email",
              phone: "tel",
              street: "street-address",
              postalCode: "postal-code",
              city: "address-level2",
            }[key]
          }
        />
      </label>
    );
  }

  return createPortal(
    <div className="projectLicenseBackdrop" onPointerDown={(event) => event.stopPropagation()}>
      <div
        className="projectLicenseDialog"
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="project-license-title"
        data-testid="project-license-dialog"
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === "Escape" && !busyRef.current) {
            event.preventDefault();
            onCancel();
          }
          if (event.key === "Tab") {
            const controls = Array.from(
              dialog.current?.querySelectorAll<HTMLElement>(
                "button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled)",
              ) ?? [],
            );
            const first = controls[0],
              last = controls[controls.length - 1];
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first?.focus();
            }
          }
        }}
      >
        <header className="projectLicenseHeader">
          <span className="projectLicenseBrand">
            <img src={`${import.meta.env.BASE_URL}images/logo.png`} alt="" />
            ESTADA
          </span>
          <button
            type="button"
            className="projectLicenseClose"
            aria-label={t("close", "Schließen")}
            disabled={busy}
            onClick={onCancel}
          >
            <X size={20} />
          </button>
        </header>
        <div className="projectLicenseContent">
        {step === "license" && (
          <>
            <div className="projectLicenseBody">
              <h2 id="project-license-title">
                {documents
                  ? t("notLicensed", "Dein Projekt ist noch nicht lizenziert.")
                  : t("continueKnxEngineering", "Weiter zum KNX-Engineering")}
              </h2>
              <p className="projectLicenseSub">
                {documents
                  ? t(
                      "documentationLicenseRequired",
                      "Um die vollständige Dokumentation deines Projektes zu erstellen, benötigst du eine Lizenz.",
                    )
                  : t(
                      "knxEngineeringIntroduction",
                      "Du verlässt die bisherige Ausstattungsplanung und startest mit der technischen KNX- & Detail-Planung. Das bedeutet mehr Einstellungsmöglichkeiten und mehr Detailtiefe als zuvor. Du kannst aus der Projektauswahl jederzeit wieder diese einfache Ansicht öffnen.",
                    )}
              </p>
              {documents && (
                <button
                  type="button"
                  className="projectLicenseButton"
                  onClick={() => {
                    onCancel();
                    state.dialogManager.openDialog(<ToShopLicensesDialog />);
                  }}
                >
                  <ShoppingBag size={18} />
                  {t("buyLicense", "Projektlizenz erwerben")}
                  <ArrowRight size={16} />
                </button>
              )}
              <section className="projectLicenseService">
                <h3>{t("freeOfferHeading", "Kostenloses Angebot anfragen:")}</h3>
                <p>
                  {t(
                    "freeOfferDescription",
                    "Fordere jetzt ein unverbindliches Angebot für dein Projekt an. Unsere KNX-Profis und Systemintegratoren begleiten dich von der lückenlosen Fachplanung über die Geräteprogrammierung bis zur Installation, alles aus einer Hand.",
                  )}
                </p>
                <ul className="projectLicenseBenefits">
                  <li>
                    <strong>{t("plugAndPlay", "Plug & Play:")}</strong>{" "}
                    {t("plugAndPlayDescription", "Wir liefern dir fertig programmierte KNX-Komponenten")}
                  </li>
                  <li>
                    <strong>{t("completePackage", "Komplettpaket:")}</strong>{" "}
                    {t("completePackageDescription", "Planung, Programmierung und Installation aus einer Hand")}
                  </li>
                  <li>
                    <strong>{t("contactPerson", "Ansprechpartner:")}</strong>{" "}
                    {t("contactPersonDescription", "Ein fester Ansprechpartner ist jederzeit für alle Fragen erreichbar")}
                  </li>
                </ul>
                <button
                  type="button"
                  className="projectLicenseButton projectLicenseButton--primary"
                  data-initial-focus
                  data-testid="project-offer-open"
                  onClick={() => setStep("contact")}
                >
                  <MessageSquare size={18} />
                  {t("requestOffer", "Unverbindliches Angebot anfragen")}
                </button>
              </section>
            </div>
            <footer className="projectLicenseFooter">
              <button
                type="button"
                className="projectLicenseButton"
                data-testid="project-license-continue"
                onClick={onContinue}
              >
                {continueLabel}
                <ArrowRight size={18} />
              </button>
            </footer>
          </>
        )}
        {step === "contact" && (
          <form
            className="projectLicenseBody"
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <h2 id="project-license-title">{t("verify", "Prüfe bitte deine Angaben.")}</h2>
            <p className="projectLicenseSub">
              {t(
                "contactHint",
                "Mit diesen Informationen kann ESTADA Dich kontaktieren und Dein Projekt einschätzen.",
              )}
            </p>
            <div className="projectLicenseFields">
              <label className="projectLicenseField--wide" htmlFor="offer-scope">
                {t("scope", "Wofür möchtest Du ein Angebot?")}
                <select
                  id="offer-scope"
                  value={scope}
                  disabled={busy}
                  data-initial-focus
                  onChange={(event) => {
                    setScope(event.target.value as OfferScope);
                  }}
                >
                  <option value="planning">{t("planning", "Planung")}</option>
                  <option value="devices">
                    {t("devices", "Planung und Lieferung programmierter Geräte")}
                  </option>
                  <option value="installation">
                    {t("installation", "Planung und vollständige Installation")}
                  </option>
                </select>
              </label>
              {field("name", t("name", "Vor- und Nachname"), "text", true)}
              {field("phone", t("phone", "Telefonnummer"), "tel")}
              {field("email", t("email", "E-Mail-Adresse"), "email")}
              {field("street", t("street", "Straße und Hausnummer"), "text", true)}
              {field("postalCode", t("postalCode", "Postleitzahl"))}
              {field("city", t("city", "Ort"))}
            </div>
            <p className="projectLicenseSmall">
              {t(
                "dataHint",
                "Deine Angaben werden für diese Anfrage an ESTADA übermittelt und im Projekt hinterlegt.",
              )}
            </p>
            {error && (
              <p className="projectLicenseError" role="alert">
                {error}
              </p>
            )}
            <div className="projectLicenseActions">
              <button
                type="button"
                className="projectLicenseButton"
                disabled={busy}
                onClick={() => {
                  setStep("license");
                  setError("");
                }}
              >
                {t("back", "Zurück")}
              </button>
              <button
                type="submit"
                className="projectLicenseButton projectLicenseButton--primary"
                disabled={busy}
                data-testid="project-offer-submit"
              >
                {busy ? t("sending", "Wird gesendet …") : t("send", "Unverbindlich anfragen")}
                <ArrowRight size={18} />
              </button>
            </div>
          </form>
        )}
        {step === "success" && (
          <div className="projectLicenseBody">
            <CheckCircle2 className="projectLicenseSuccess" size={32} />
            <h2 id="project-license-title">{t("thanks", "Vielen Dank für Deine Anfrage.")}</h2>
            <p>{t("sent", "Deine Anfrage wurde erfolgreich an ESTADA gesendet.")}</p>
            <p className="projectLicenseSub">
              {contact.name}
              <br />
              {contact.email}
              <br />
              {contact.phone}
              <br />
              {contact.street}
              <br />
              {contact.postalCode} {contact.city}
            </p>
            <p className="projectLicenseSmall">
              {sent?.sentAt && new Date(sent.sentAt).toLocaleString()}
            </p>
            {saveWarning && (
              <p role="alert" className="projectLicenseError">
                {t(
                  "saveWarning",
                  "Die Anfrage wurde gesendet, konnte aber noch nicht dauerhaft im Projekt gespeichert werden. Bitte speichere das Projekt erneut.",
                )}
              </p>
            )}
            <button
              type="button"
              className="projectLicenseButton"
              data-initial-focus
              onClick={onContinue}
            >
              {continueLabel}
              <ArrowRight size={18} />
            </button>
          </div>
        )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
